import React from 'react';
import PropTypes from 'prop-types';

import { ElectionType, EntityType, RoundBlockType, RoundType, TurnoutDataType } from '../../../typings/parties-ranking';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import ErrorBoundary from '../error-boundary/error-boundary';
import ErrorComponent from '../../atoms/error/error';
import t from '../../../labels/labelling-utils';
import Text from '../../atoms/text/text';
import LastUpdate from '../../molecules/last-update/last-update';
import DataSource from '../../molecules/data-source/data-source';
import PartiesRankingTable, { IRowExtended } from '../../organisms/parties-ranking-table/parties-ranking-table';
import { isValidNumber, numberFormat } from '../../../utils/numbers';
import { stringToLispCase, uppercaseFirstLetter } from '../../../utils/string-utils';

import { transform } from './parties-ranking-block-helper';

import './parties-ranking-block.scss';

export interface IPartiesRanking extends TurnoutDataType {
  entity: {
    type: EntityType;
    name: string;
  };
  type: ElectionType;
  lastUpdate: string;
  rounds: RoundType[];
  isEuropean?: boolean;
}

export interface IPartiesRankingBlock {
  roundBlocks: Array<RoundBlockType>;
  isoTimestamp: string;
  entityName: string;
  electionType: ElectionType;
  turnoutData: TurnoutDataType;
  isEuropean?: boolean;
}

const BLOCK_IDS_PREFIX = '';

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const classNames = (): IClassNames => {
  return makeClassNames({
    anchorsBlock: 'anchors-block',
    anchorBallot: 'anchor-ballot',
    partiesRankingBlockClassName: 'parties-ranking-block',
    preTitleClassName: 'pre-title',
    roundBlock: 'round-block',
    roundBlockTitle: 'round-block-title',
    preTableBlock: 'pre-table-block',
    legend: 'elected-legend',
    entityNameTitle: 'entity-name-title',
    turnoutText: 'turnout-text',
  });
};

const getRoundTitle = (round: RoundBlockType) => {
  const cn = classNames();

  const roundName = round.roundName;

  const roundNameToClassname = stringToLispCase({
    string: roundName,
    prefix: BLOCK_IDS_PREFIX,
  });
  const roundNameCapitalized = uppercaseFirstLetter(roundName);

  return (
    <div id={roundNameToClassname} className={cn.roundBlockTitle}>
      <Text tag="p" fontSize="16--22" fontWeight="500">
        {roundNameCapitalized}
      </Text>
    </div>
  );
};

const getLegend = (rowBlock: RoundBlockType) => {
  const cn = classNames();
  const roundData = rowBlock.roundData;

  return (
    roundData.find((currentRowBlock: Array<IRowExtended>) =>
      currentRowBlock.find((currentRow: IRowExtended) => currentRow.isElected)
    ) && (
      <Text tag="p" fontSize="12--14" className={cn.legend}>
        {t('titles-labels.legend')} <span>{t('titles-labels.elected')}</span>
      </Text>
    )
  );
};

/**
 * IPartiesRanking interface
 * @param payload - The Object containing original Parties Ranking Data
 */
const PartiesRankingBlock: React.FC<IPartiesRanking> = (payload: IPartiesRanking) => {
  const { roundBlocks, isoTimestamp, turnoutData, isEuropean } = transform(payload);
  const { percentageTurnout, partialPollingStations, totalPollingStations } = turnoutData;

  const getTurnoutText = () => {
    const cn = classNames();

    const formattedPercentageTurnout = isValidNumber(percentageTurnout)
      ? `${numberFormat(Number(percentageTurnout))}%`
      : '-';
    const formattedPartialPollingStations = isValidNumber(partialPollingStations)
      ? numberFormat(Number(partialPollingStations))
      : '-';
    const formattedTotalPollingStations = isValidNumber(totalPollingStations)
      ? numberFormat(Number(totalPollingStations))
      : '-';

    return (
      <Text tag="p" fontSize="12" className={cn.turnoutText}>
        {percentageTurnout && (
          <React.Fragment>
            {t('titles-labels.percentageTurnout')}: <span>{formattedPercentageTurnout}</span> —
          </React.Fragment>
        )}
        {t('titles-labels.scrutinizedPollingStations')}: <span>{formattedPartialPollingStations}</span>{' '}
        {t('titles-labels.on')} {formattedTotalPollingStations}
      </Text>
    );
  };

  const getAnchorsBlock = () =>
    roundBlocks.length > 1 && (
      <div className={cn.anchorsBlock}>
        {roundBlocks.map((roundBlock: RoundBlockType, index: number) => {
          const roundNameId = stringToLispCase({
            string: roundBlock.roundName,
            prefix: `#${BLOCK_IDS_PREFIX}`,
          });
          const roundName = roundBlock.roundName ? uppercaseFirstLetter(roundBlock.roundName) : '';

          return (
            <Text key={index} className={cn.anchorBallot} fontSize="12" fontWeight="500" tag="a" href={roundNameId}>
              {roundName}
            </Text>
          );
        })}
      </div>
    );

  // Classes
  const cn = classNames();

  return (
    <div className={cn.partiesRankingBlockClassName}>
      <ErrorBoundary /* hasError={} */ errorComponent={getErrorComponent()}>
        {getAnchorsBlock()}
        <LastUpdate isoTimestamp={isoTimestamp} />
        <div className={cn.preTitleClassName}>
          {getTurnoutText()}
          <DataSource dataSource={t('header.block.data.ministry')} />
        </div>
        {roundBlocks.map((roundBlock: RoundBlockType, index: number) => (
          <div key={index} className={cn.roundBlock}>
            <div className={cn.preTableBlock}>
              {roundBlocks.length > 1 && getRoundTitle(roundBlock)}
              {getLegend(roundBlock)}
            </div>
            <PartiesRankingTable isEuropean={Boolean(isEuropean)} rows={roundBlock.roundData} showTitles={false} />
          </div>
        ))}
      </ErrorBoundary>
    </div>
  );
};

PartiesRankingBlock.propTypes = {
  entity: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOf<EntityType>(['regione', 'comune']).isRequired,
  }).isRequired,
  lastUpdate: PropTypes.string.isRequired,
  partialPollingStations: PropTypes.number,
  percentageTurnout: PropTypes.number,
  rounds: PropTypes.arrayOf(
    PropTypes.shape({
      candidates: PropTypes.arrayOf(
        PropTypes.shape({
          candidateName: PropTypes.string.isRequired,
          candidateSurname: PropTypes.string.isRequired,
          candidateVotes: PropTypes.number,
          candidateVotesPercentage: PropTypes.number,
          elected: PropTypes.bool.isRequired,
          parties: PropTypes.arrayOf(
            PropTypes.shape({
              partyName: PropTypes.string.isRequired,
              partySeats: PropTypes.number,
              partyVotes: PropTypes.number,
              partyVotesPercentage: PropTypes.number,
            }).isRequired
          ).isRequired,
        }).isRequired
      ).isRequired,
      round: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  totalPollingStations: PropTypes.number,
  type: PropTypes.oneOf<ElectionType>(['regionali', 'comunali']).isRequired,
};

export default PartiesRankingBlock;
